<template>
    <div v-if="store.items.length > 1" class="app-picker-sidebar px-3 pb-2">
        <v-select
            v-model="value"
            :items="store.items"
            item-title="name"
            item-value="id"
            :label="$t('quickInfo.title')"
            density="compact"
            variant="outlined"
            hide-details
            prepend-inner-icon="mdi-application"
        />
    </div>
</template>

<script lang="ts">
import {defineComponent, ref, watch} from "vue";
import {useAuthorizationStore} from "@/stores/AuthorizationStore/AuthorizationStore";
import {useApiCredentials} from "@/composables/useApiCredentials";
import {useWorkflowStore} from "@/stores/WorkflowStore/WorkflowStore";
import {useKnowledgeBaseStore} from "@/stores/KnowledgeBaseStore/KnowledgeBaseStore";
import {useAgentStore} from "@/stores/AgentStore/AgentStore";
import {useGuardrailStore} from "@/stores/GuardrailStore/GuardrailStore";
import {IApplicationCredentials} from "@/stores/AuthorizationStore/interfaces/IApplicationCredentials";

export default defineComponent({
    setup() {
        const store = useAuthorizationStore()
        const agentStore = useAgentStore()
        const workflowStore = useWorkflowStore()
        const kbStore = useKnowledgeBaseStore()
        const guardrailStore = useGuardrailStore()
        const {guid, apikey, setRootCredentials, appReady} = useApiCredentials()

        if (store.items && store.activeApp == undefined){
            // Try to restore from sessionStorage
            const storedAppId = sessionStorage.getItem('kmls_active_app_id')
            if (storedAppId) {
                const storedItem = store.items.find(i => i.id === Number(storedAppId))
                if (storedItem) {
                    store.activeApp = storedItem as IApplicationCredentials
                    guid.value = storedItem.guid
                    apikey.value = storedItem.apikey
                    setRootCredentials(storedItem.guid, storedItem.apikey)
                }
            }
            if (!store.activeApp) {
                store.activeApp = store.items[0] as IApplicationCredentials
            }
        }
        const value = ref<number | undefined>(store.activeApp?.id);
        watch (store.items, () => {
            if (value.value === undefined){
                value.value = store.items[0].id
            }
        })
        watch(value, async () => {
            let item = store.items.find(i => i.id === value.value)
            if (item) {
                guid.value = item.guid
                apikey.value = item.apikey
                store.activeApp = item as IApplicationCredentials
                // Also update root credentials so stores using rootGuid/rootAuth work correctly
                setRootCredentials(item.guid, item.apikey)
                // Persist selected app in browser session
                sessionStorage.setItem('kmls_active_app_id', String(item.id))
            }
            // Acquire tokens for the selected app (both root and user)
            await store.aquire_token(guid.value, true)
            await store.aquire_token(guid.value, false)
            // Load current app permissions
            await store.currentApp()
            // Reload app-specific data.
            // Always reload enabled services; clear store data for
            // disabled ones so stale items from the previous app
            // never remain visible.
            const reloads: Promise<void>[] = [
                agentStore.get_available_models().then(() => {}),
            ]
            if (store.hasService('agent')) {
                reloads.push(agentStore.loadSubAgents())
            } else {
                agentStore.items = []
            }
            if (store.hasService('workflow')) {
                reloads.push(workflowStore.loadWorkflows())
                reloads.push(workflowStore.loadApprovalStatuses())
            } else {
                workflowStore.workflows = []
            }
            if (store.hasService('knowledge_base')) {
                reloads.push(kbStore.loadKnowledgeBases())
                reloads.push(kbStore.loadAvailableConfigs())
            } else {
                kbStore.knowledgeBases = []
            }
            if (store.hasService('guardrails')) {
                reloads.push(guardrailStore.loadGuardrails())
            } else {
                guardrailStore.guardrails = []
            }
            await Promise.all(reloads)
            // Signal CrudStores to reload now that tokens are valid
            appReady.value++
        })
        return {
            value,
            store
        }
    }
})
</script>

<style scoped lang="scss">
.app-picker-sidebar {
    margin-top: 4px;
}
</style>